'use client';

import {
  Anchor,
  Avatar,
  Badge,
  Divider,
  Drawer,
  NavLink,
  Stack,
} from '@mantine/core';

import { Footer, LogoutButton } from '@/components';
import { useAuth } from '@/contexts';
import { trackEvent } from '@/utils/analytics/trackEvent';

import styles from './NavBar.module.css';

interface NavBarProps {
  isOpen: boolean;
  onClose: () => void;
}

const NavBar: React.FC<NavBarProps> = ({ isOpen, onClose }) => {
  const { isLoggedIn, currentEntityId, currentEntityProfileImageUrl } =
    useAuth();

  const handleNavClick = (path: string) => {
    trackEvent({
      category: 'navigation',
      action: 'nav_click',
      label: path,
    });
    onClose();
  };

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      position="right"
      size="xs"
      withCloseButton={false}
      classNames={{ content: styles.aside }}
      zIndex={1000}
    >
      <Stack
        gap="xs"
        align="flex-end"
        justify="flex-end"
        ta="right"
        className={styles.navStack}
      >
        {isLoggedIn && (
          <>
            <Anchor
              href={`/profiles/${currentEntityId}`}
              className={styles.avatar}
              onClick={() => handleNavClick(`/profiles/${currentEntityId}`)}
            >
              <Avatar
                src={currentEntityProfileImageUrl}
                size="62px"
                variant="profile"
                alt={'Profile'}
              />
            </Anchor>
            <NavLink
              href={`/profiles/${currentEntityId}`}
              label="Your Profile"
              className={styles.navLink}
              onClick={() => handleNavClick(`/profiles/${currentEntityId}`)}
            />
            <NavLink
              href="/voter-guide"
              label="Voter Guide"
              leftSection={
                <Badge
                  size="sm"
                  variant="filled"
                >
                  COMING SOON
                </Badge>
              }
              className={styles.navLink}
              onClick={() => handleNavClick('/voter-guide')}
            />
            <NavLink
              href={`/profiles/${currentEntityId}/topics/create`}
              label="Create New Page"
              className={styles.navLink}
              onClick={() =>
                handleNavClick(`/profiles/${currentEntityId}/topics/create`)
              }
            />
            <NavLink
              href="/verify"
              label="Verify Your Vote"
              className={styles.navLink}
              onClick={() => handleNavClick('/verify')}
            />
          </>
        )}
        <NavLink
          href="/guide"
          label="Getting Started Guide"
          className={styles.navLink}
          onClick={() => handleNavClick('/faq')}
        />
        {isLoggedIn ? (
          <LogoutButton onlyUseIcon={false} />
        ) : (
          <NavLink
            href="/login"
            label="Login"
            className={styles.navLink}
            onClick={() => handleNavClick('/login')}
          />
        )}
      </Stack>
      <Stack
        className={styles.footer}
        gap="md"
      >
        <Divider
          color="var(--mantine-color-primary-9)"
          opacity={0.3}
          className={styles.footerDivider}
        />
        <Footer />
      </Stack>
    </Drawer>
  );
};

export { NavBar };
